(ns app.front.ui.checkbox-tree
  (:require [app.front.ui.error-boundary :as eb]
            [app.front.util :refer [for-frag]]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn checkbox-tree-node
  [{:keys                         [tr level expand-evt select-evt children-sub
                                   children-loading-sub selectable-sub render-content
                                   item->label item->selected? item->partially-selected? item->id]
    :or                           {level 0}
    {:keys [id content] :as item} :item
    :as                           ctx}]
  (r/with-let [*expanded? (r/atom false)]
    [eb/error-boundary
     (let [id                (if item->id (item->id item) id)
           children          (when children-sub
                               (<sub (conj children-sub item)))
           children-loading? (boolean (when children-loading-sub
                                        (<sub (conj children-loading-sub item))))
           checked           (boolean (item->selected? item))
           indeterminate     (boolean (and (not checked)
                                           item->partially-selected?
                                           (item->partially-selected? item)))
           disabled?         (boolean (when selectable-sub
                                        (not (<sub (conj selectable-sub item)))))
           expandable?       (or @*expanded? (seq children))
           on-click          (fn [_]
                               (swap! *expanded? not)
                               (when (and @*expanded? expand-evt)
                                 (doseq [child children]
                                   (>evt (conj expand-evt child)))))]
       [:li.flex.flex-col
        [:div.flex.flex-col.border-b.border-solid.border-ui-gray.p-2
         {:style {:padding-left (str (+ 0.5 (* level 2)) "rem")}}
         [:div.flex.items-center.justify-start
          [:div.flex.items-center.justify-center {:style {:width "40px" :height "40px"}}
           (cond
             (or @*expanded? (seq children))
             (cc/button {:kind             :ghost
                         :icon-position    :right
                         :icon-description (if *expanded?
                                             (tr [:carbon.table.row.collapse])
                                             (tr [:carbon.table.row.expand]))
                         :class-name       "only-icon-button mr-2"
                         :size             :field
                         :icon             (if @*expanded?
                                             i/chevron-up
                                             i/chevron-down)
                         :on-click         on-click}
                        "")
             children-loading?
             [:div (cc/inline-loading {})])]
          [:div.flex-0
           (cc/checkbox {:id            (str id "-selected")
                         :label-text    (item->label item)
                         :checked       checked
                         :indeterminate indeterminate
                         :disabled      disabled?
                         :on-change     #(when select-evt
                                           (>evt (conj select-evt item %)))})]
          [:div.flex-1.w-full (when expandable?
                                {:on-click on-click
                                 :style    {:height "40px"}
                                 :class    "cursor-pointer"})]]
         (when content
           [:div.border.border-solid.border-ui-light-gray.rounded.p-2
            {:style {:margin-left "40px"}}
            [render-content item]])]
        (when (and @*expanded? children)
          [:ol
           (for-frag [child children]
             [checkbox-tree-node (merge ctx {:item  child
                                             :level (inc level)})])])])]))

(defn checkbox-tree
  "A tree view component where each node is expandable and shows a chevron if it has child nodes.
   Each node also has a checkbox allowing it to be selected. Selecting a node will select (or unselect)
   all of its children. Child nodes can be loaded lazily.

   Options:


   `:tr`                        tr-fn
   `:items`                     sequence of top level items
   `:item->label`               item -> str fn, returns the label to be rendered for the checkboxes
   `:item->selected?`           item -> bool fn, return true if item (checkbox) is fully checked
   `:item->partially-selected?` item -> bool fn, return true if item (checkbox) is partially checked / indeterminate
   `:item->id`                  item -> id fn, defaults to :id
   `:render-content`            component fn for rendering :content of tree items (required only if :content present)
   `:children-sub`              sub vector, subscription should return children of current item which is added to the vector
   `:children-loading-sub`      sub vector, return true if current item's children are loading, current item added to the vector
   `:selectable-sub`            sub vector, return true if current node can be selected, the item map is added to the vector
   `:select-evt`                event vector, fired when the node is selected or deselected, with the item and boolean added
   `:expand-evt`                event vector, fired when node is expanded for each current node child.
                                top level item id and the child are added to vector

   Subscriptions and event vectors are optional (though this will not do much without them).

   Item maps must contain at least :id key and probably something to create the labels."
  [{:keys [tr items expand-evt select-evt children-sub children-loading-sub selectable-sub
           render-content item->label item->selected? item->partially-selected? item->id class]}]
  (when (seq items)
    [:div.border.border-solid.border-ui-gray.rounded.w-full {:class class}
     [:ol
      (for-frag [item items]
        [checkbox-tree-node (util/mmap {:expand-evt (when expand-evt (conj expand-evt (:id item)))}
                                       tr item select-evt children-sub children-loading-sub selectable-sub
                                       render-content item->label item->selected? item->partially-selected?
                                       item->id)])]]))
